import { useInView } from '@/hooks/useInView';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import HeroSection from '@/components/feature/HeroSection';
import AboutSection from '@/components/feature/AboutSection';
import CategoryGrid from '@/components/feature/CategoryGrid';
import ShopCategories from '@/components/feature/ShopCategories';
import ComparisonTable from '@/components/feature/ComparisonTable';
import InternalLinkingHub from '@/components/feature/InternalLinkingHub';
import TrustSection from '@/components/feature/TrustSection';
import SEOArticlesSection from '@/components/feature/SEOArticlesSection';
import TestimonialSection from '@/components/feature/TestimonialSection';
import CTASection from '@/components/feature/CTASection';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheet.lifestyle';

const homepageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Mulebuy Spreadsheet Hub',
  url: siteUrl,
  description: 'The most trusted directory for Mulebuy spreadsheet resources, comparisons, and guides in 2026.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

function AnimatedSection({ children }: { children: React.ReactNode }) {
  const { ref, inView } = useInView(0.08);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <main className="min-h-screen bg-background-50">
      <SEO
        title="Mulebuy Spreadsheet 2026 – Best Updated Lists, Comparisons &amp; Safety Guide"
        description="Discover the most complete Mulebuy spreadsheet resources in 2026. Compare trusted spreadsheet tools, learn how they work, and access updated guides and alternatives in one place."
        keywords="mulebuy spreadsheet, mulebuy spreadsheet 2026, spreadsheet comparison, sourcing tool, data structure guide"
        canonical={`${siteUrl}/`}
        ogType="website"
        jsonLd={homepageJsonLd}
      />
      <Navbar />
      <HeroSection />
      <AnimatedSection><AboutSection /></AnimatedSection>
      <AnimatedSection><CategoryGrid /></AnimatedSection>
      <AnimatedSection><ShopCategories /></AnimatedSection>
      <AnimatedSection><ComparisonTable /></AnimatedSection>
      <AnimatedSection><InternalLinkingHub /></AnimatedSection>
      <AnimatedSection><SEOArticlesSection /></AnimatedSection>
      <AnimatedSection><TrustSection /></AnimatedSection>
      <AnimatedSection><TestimonialSection /></AnimatedSection>
      <AnimatedSection><CTASection /></AnimatedSection>
      <Footer />
    </main>
  );
}